/**
 * MeetingNotesWidget — "Meeting Notes". Laatste opgenomen meetings (FF Recorder)
 * van de gebruiker, met datum en duur. Klik → Meeting Dashboard.
 * Databron: AI Engine GET /api/v1/meetings/?user_email=…
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T } from './widgetTokens';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return `vandaag ${d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

export default function MeetingNotesWidget() {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const [meetings, setMeetings] = useState(null); // null = laden
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!userEmail) { setMeetings([]); return undefined; }
    authFetch(`${AI_ENGINE_URL}/api/v1/meetings/?user_email=${encodeURIComponent(userEmail)}&limit=6`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch failed'))))
      .then((d) => { if (active) setMeetings(Array.isArray(d) ? d : []); })
      .catch(() => { if (active) { setError(true); setMeetings([]); } });
    return () => { active = false; };
  }, [userEmail]);

  if (meetings === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} sx={{ color: T.outlineVariant }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Meetings konden niet geladen worden.</Typography>;
  }
  if (meetings.length === 0) {
    return (
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
        Nog geen meetings — neem er een op met de FF Recorder.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {meetings.slice(0, 6).map((m) => {
        const geenAudio = (m.title || '').startsWith('⚠️');
        return (
          <Box key={m.id} component="a" href="/MeetingDashboard" sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none',
            bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.25,
            borderLeft: `3px solid ${geenAudio ? T.amber : T.tertiary}`,
            transition: 'background 0.15s', '&:hover': { bgcolor: T.surfaceHigh },
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text }}>
                {m.title || 'Meeting'}
              </Typography>
              <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.15 }}>
                {[fmtDate(m.meeting_date), m.duration ? `${m.duration} min` : ''].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
